import React, {
  useCallback,
  useRef,
  useMemo,
  useContext,
  MouseEventHandler,
  startTransition,
} from 'react';
import classnames from 'classnames';
import throttle from 'lodash/throttle';
import { useTranslation } from 'react-i18next';
import { Url } from '@wix/da-url';
import { useOnOutsideClick } from '@wix/da-hooks/pkg/useOnOutsideClick';
import NotificationIcon from '@wix/da-ds/pkg/Icons/24x24/Notification';
import NotificationOutlineIcon from '@wix/da-ds/pkg/Icons/24x24/NotificationOutline';
import { BiData, TopNavClickBiEvent } from '@wix/da-bi/pkg/events';
import type { BiEvent } from '@wix/da-bi/pkg/events';
import IconButtonWithNotification from '@wix/da-ds/pkg/Buttons/IconButtonWithNotification';
import { NCContext } from '@wix/da-react-context/pkg/NCContext';
import { useDropdownHover } from '../../hooks/useDropdownHover';
import { yieldToMain } from '@wix/da-hooks/pkg/yieldToMain';
import s from './NotificationsDropdownWrapper.scss';

export interface Props {
  feedbackCount: number;
  className?: string;
  iconClassName?: string;
  numberClassName?: string;
  biData?: BiEvent;
  isFeedbackPageOpen?: boolean;
  isNotificationsDropdownOpen: boolean;
  ['aria-label']?: string;
  openNotificationsDropdown: () => void;
  closeNotificationsDropdown: () => void;
  toggleFeedbackPage: () => void;
  toggleBurgerMenu: (open: boolean) => void;
  iconWithOutline?: boolean;
}

const NotificationsDropdownWrapper: React.FC<Props> = ({
  feedbackCount,
  className,
  iconClassName,
  numberClassName,
  openNotificationsDropdown,
  closeNotificationsDropdown,
  isFeedbackPageOpen,
  isNotificationsDropdownOpen,
  toggleFeedbackPage,
  toggleBurgerMenu,
  'aria-label': ariaLabel,
  iconWithOutline,
}) => {
  const { t } = useTranslation();
  const { DrawerComponent, isStandalonePage } = useContext(NCContext);
  const { handleDropdownHover, handleDropdownLeave } = useDropdownHover({
    isDropdownOpen: isNotificationsDropdownOpen,
    openDropdown: openNotificationsDropdown,
    closeDropdown: closeNotificationsDropdown,
  });

  // no point to spamming this if our guest has twitchy hands
  const throttledMouseLeave = useMemo(
    () => throttle(handleDropdownLeave, 300),
    [handleDropdownLeave]
  );
  const rootRef = useRef<HTMLDivElement>(null);
  const onClose = useCallback(() => {
    throttledMouseLeave();
  }, [throttledMouseLeave]);
  const handleClickOutside = useCallback(() => {
    if (!isNotificationsDropdownOpen) {
      return;
    }
    onClose();
  }, [isNotificationsDropdownOpen, onClose]);
  useOnOutsideClick(rootRef, handleClickOutside);

  const handleIconClick: MouseEventHandler = useCallback(
    e => {
      if (!isStandalonePage) {
        // We can use startTransition outright. useTransition's pending state is unnecessary since
        // a loading screen gets mounted immediately.
        startTransition(() => {
          yieldToMain(() => {
            toggleBurgerMenu(false); // Close network bar, if it's open
            toggleFeedbackPage();
          });
        });
      }

      // We put an `href` attribute on the element that calls this so users can open notifications in a new tab,
      // so we prevent default to avoid the standard handling of an anchor tag click since we're duper browsing.
      e.preventDefault();
    },
    [isStandalonePage, toggleFeedbackPage, toggleBurgerMenu]
  );

  return (
    <div
      className={className}
      onMouseEnter={() => {
        if (!isFeedbackPageOpen && !isStandalonePage) {
          handleDropdownHover();
        }
      }}
      onMouseLeave={() => handleDropdownLeave()}
      ref={rootRef}
    >
      <IconButtonWithNotification
        icon={iconWithOutline ? NotificationOutlineIcon : NotificationIcon}
        size="medium"
        notificationColor="red"
        iconButtonClassName={iconClassName}
        numberClassName={numberClassName}
        count={feedbackCount}
        biData={BiData<TopNavClickBiEvent>({
          evid: 700,
          link_name: 'notifications',
          indicator: feedbackCount,
        })}
        href={
          isStandalonePage || isFeedbackPageOpen
            ? undefined
            : Url.feedbackLink()
        }
        onClick={handleIconClick}
        aria-label={t('siteHeader.a11y.cart.withItems', {
          formattedCount: feedbackCount,
        })}
      />
      {DrawerComponent && !isStandalonePage && (
        <div
          role="dialog"
          id="site-header-notifications"
          className={s['notifications-dropdown-wrapper']}
        >
          <div
            className={classnames(
              s['container'],
              isNotificationsDropdownOpen && s['active']
            )}
          >
            {isNotificationsDropdownOpen && (
              <DrawerComponent
                isDropdown
                openFeedbackPage={() =>
                  toggleFeedbackPage && toggleFeedbackPage()
                }
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
};
NotificationsDropdownWrapper.displayName = 'NotificationsDropdownWrapper';

export default NotificationsDropdownWrapper;
